import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import type {
  ContractPackageStatusDto,
  SupplementalAttemptDto,
} from "../dto/contract-package.dto"
import { contractPackageService } from "../services/contract-package.service"

/**
 * Who sends the main contract for this Proposal, and the state of any
 * supplemental package Vision sends on a finance partner's behalf.
 *
 * A failed automatic send never retries on its own, so this does not poll: it
 * only changes when a provider event arrives or the seller acts.
 */
export function useContractPackage(
  proposalId: string | undefined,
): UseQueryResult<ContractPackageStatusDto> {
  return useQuery({
    queryKey: queryKeys.contracting.package(proposalId ?? ``),
    queryFn: () => {
      if (!proposalId) {
        throw new Error(`Proposal id is required`)
      }

      return contractPackageService.getPackageStatus(proposalId)
    },
    enabled: Boolean(proposalId),
    retry: 1,
    staleTime: 30_000,
  })
}

export function useRetrySupplementalSend(
  proposalId: string | undefined,
  options?: { onSettled?: () => Promise<void> | void },
): UseMutationResult<SupplementalAttemptDto, unknown, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (attemptId: string) =>
      contractPackageService.retrySupplementalSend(attemptId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.contracting.package(proposalId ?? ``),
      })
      // A successful send produces a new package the contract list must show.
      void options?.onSettled?.()
    },
  })
}
